import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, TextField, CircularProgress, Alert,
  Paper, IconButton, Tooltip, Chip, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../axios';
import ModuleHeader from '../../components/ModuleHeader';

const TEAL = '#00F1A7';
const GREEN = '#10B981';
const RED = '#EF4444';
const BLUE = '#3B82F6';

const tabSx = {
  mb: 2,
  '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, textTransform: 'none' },
  '& .Mui-selected': { color: 'text.primary' },
};

export default function CategoriasFinanceiro({ inTab } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const tabPaths = ['/financeiro', '/financeiro/lancamentos', '/financeiro/categorias', '/financeiro/contas'];
  const tabIndex = Math.max(0, tabPaths.indexOf(location.pathname));

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const [categorias, setCategorias] = useState([]);

  const [addingTipo, setAddingTipo] = useState(null);
  const [novaNome, setNovaNome] = useState('');
  const [novoTipo, setNovoTipo] = useState('receita');

  const [editingId, setEditingId] = useState(null);
  const [editNome, setEditNome] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(null);

  const [centros, setCentros] = useState([]);
  const [editingCentroId, setEditingCentroId] = useState(null);
  const [editCentroNome, setEditCentroNome] = useState('');
  const [addingCentro, setAddingCentro] = useState(false);
  const [novoCentroNome, setNovoCentroNome] = useState('');
  const [confirmDeleteCentro, setConfirmDeleteCentro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const [catRes, cenRes] = await Promise.all([
        api.get('/financeiro/categorias'),
        api.get('/financeiro/centros-custo')
      ]);
      setCategorias(catRes.data);
      setCentros(cenRes.data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dados');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleCreate = async () => {
    setErro('');
    if (!novaNome.trim()) { setErro('Preencha o nome'); return; }
    try {
      await api.post('/financeiro/categorias', { nome: novaNome.trim(), tipo: novoTipo });
      setSucesso('Categoria criada!');
      setNovaNome('');
      setAddingTipo(null);
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao criar categoria');
    }
  };

  const handleDelete = async (id) => {
    setErro('');
    try {
      await api.delete(`/financeiro/categorias/${id}`);
      setSucesso('Categoria excluída!');
      setConfirmDelete(null);
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const handleSaveEdit = async (id) => {
    setErro('');
    if (!editNome.trim()) return;
    try {
      await api.put(`/financeiro/categorias/${id}`, { nome: editNome.trim() });
      setSucesso('Categoria atualizada!');
      setEditingId(null);
      setEditNome('');
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao atualizar');
    }
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditNome(cat.nome);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNome('');
  };

  const handleCreateCentro = async () => {
    setErro('');
    if (!novoCentroNome.trim()) { setErro('Preencha o nome'); return; }
    try {
      await api.post('/financeiro/centros-custo', { nome: novoCentroNome.trim() });
      setSucesso('Centro de Custo criado!');
      setNovoCentroNome('');
      setAddingCentro(false);
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao criar centro de custo');
    }
  };

  const handleDeleteCentro = async (id) => {
    setErro('');
    try {
      await api.delete(`/financeiro/centros-custo/${id}`);
      setSucesso('Centro de Custo excluído!');
      setConfirmDeleteCentro(null);
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  const handleSaveEditCentro = async (id) => {
    setErro('');
    if (!editCentroNome.trim()) return;
    try {
      await api.put(`/financeiro/centros-custo/${id}`, { nome: editCentroNome.trim() });
      setSucesso('Centro de Custo atualizado!');
      setEditingCentroId(null);
      setEditCentroNome('');
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao atualizar');
    }
  };

  const startEditCentro = (c) => {
    setEditingCentroId(c.id);
    setEditCentroNome(c.nome);
  };

  const cancelEditCentro = () => {
    setEditingCentroId(null);
    setEditCentroNome('');
  };

  const categoriasReceita = categorias.filter(c => c.tipo === 'receita');
  const categoriasDespesa = categorias.filter(c => c.tipo === 'despesa');

  const renderCategoriaList = (lista, tipo) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {lista.map(cat => (
        <Paper
          key={cat.id}
          variant="outlined"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1, px: 2,
            borderColor: editingId === cat.id ? TEAL : 'divider',
          }}
        >
          {editingId === cat.id ? (
            <>
              <TextField
                size="small"
                value={editNome}
                onChange={e => setEditNome(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(cat.id); if (e.key === 'Escape') cancelEdit(); }}
                autoFocus
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
              />
              <IconButton size="small" onClick={() => handleSaveEdit(cat.id)} sx={{ color: GREEN }}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={cancelEdit} sx={{ color: '#999' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{cat.nome}</Typography>
              <Chip
                label={tipo === 'receita' ? 'Receita' : 'Despesa'}
                size="small"
                sx={{
                  bgcolor: tipo === 'receita' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                  color: tipo === 'receita' ? GREEN : RED,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => startEdit(cat)} sx={{ color: TEAL }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir">
                <IconButton size="small" onClick={() => setConfirmDelete({ id: cat.id, nome: cat.nome })} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Paper>
      ))}
      {lista.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nenhuma categoria cadastrada.
        </Typography>
      )}

      {addingTipo === tipo ? (
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, px: 2, borderColor: TEAL }}>
          <TextField
            size="small"
            value={novaNome}
            onChange={e => setNovaNome(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setAddingTipo(null); }}
            autoFocus
            placeholder="Nome da categoria"
            fullWidth
            sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
          />
          <IconButton size="small" onClick={handleCreate} sx={{ color: GREEN }}>
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => { setAddingTipo(null); setNovaNome(''); }} sx={{ color: '#999' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      ) : (
        <Button
          startIcon={<AddIcon />}
          onClick={() => { setAddingTipo(tipo); setNovoTipo(tipo); }}
          sx={{ alignSelf: 'flex-start', color: TEAL, '&:hover': { bgcolor: 'rgba(139,92,246,0.06)' } }}
        >
          Nova Categoria
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <Container maxWidth="lg" sx={inTab ? { px: 0 } : { py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        {!inTab && <ModuleHeader module="Financeiro" title="Categorias" />}

        {!inTab && (
          <Tabs
            value={tabIndex}
            onChange={(_, v) => navigate(tabPaths[v])}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}
            sx={tabSx}
          >
            <Tab label="Dashboard" />
            <Tab label="Lançamentos" />
            <Tab label="Categorias" />
            <Tab label="Contas" />
          </Tabs>
        )}

        {inTab && <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Categorias</Typography>}
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <Accordion defaultExpanded sx={{ mb: 2, borderRadius: '5px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'rgba(16,185,129,0.06)', borderLeft: `4px solid ${GREEN}`, borderRadius: '5px 5px 0 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: GREEN }}>
                    Receitas
                  </Typography>
                  <Chip label={categoriasReceita.length} size="small" sx={{ bgcolor: GREEN, color: '#fff', fontWeight: 700 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>
                {renderCategoriaList(categoriasReceita, 'receita')}
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ mb: 2, borderRadius: '5px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'rgba(239,68,68,0.06)', borderLeft: `4px solid ${RED}`, borderRadius: '5px 5px 0 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: RED }}>
                    Despesas
                  </Typography>
                  <Chip label={categoriasDespesa.length} size="small" sx={{ bgcolor: RED, color: '#fff', fontWeight: 700 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>
                {renderCategoriaList(categoriasDespesa, 'despesa')}
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ mb: 2, borderRadius: '5px !important', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'rgba(59,130,246,0.06)', borderLeft: `4px solid ${BLUE}`, borderRadius: '5px 5px 0 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BLUE }}>
                    Centros de Custo
                  </Typography>
                  <Chip label={centros.length} size="small" sx={{ bgcolor: BLUE, color: '#fff', fontWeight: 700 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {centros.map(c => (
                    <Paper
                      key={c.id}
                      variant="outlined"
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        p: 1, px: 2,
                        borderColor: editingCentroId === c.id ? BLUE : 'divider',
                      }}
                    >
                      {editingCentroId === c.id ? (
                        <>
                          <TextField
                            size="small"
                            value={editCentroNome}
                            onChange={e => setEditCentroNome(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSaveEditCentro(c.id); if (e.key === 'Escape') cancelEditCentro(); }}
                            autoFocus
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
                          />
                          <IconButton size="small" onClick={() => handleSaveEditCentro(c.id)} sx={{ color: GREEN }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditCentro} sx={{ color: '#999' }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{c.nome}</Typography>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => startEditCentro(c)} sx={{ color: BLUE }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" onClick={() => setConfirmDeleteCentro({ id: c.id, nome: c.nome })} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Paper>
                  ))}
                  {centros.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      Nenhum centro de custo cadastrado.
                    </Typography>
                  )}

                  {addingCentro ? (
                    <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, px: 2, borderColor: BLUE }}>
                      <TextField
                        size="small"
                        value={novoCentroNome}
                        onChange={e => setNovoCentroNome(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleCreateCentro(); if (e.key === 'Escape') { setAddingCentro(false); setNovoCentroNome(''); } }}
                        autoFocus
                        placeholder="Nome do centro de custo"
                        fullWidth
                        sx={{ '& .MuiOutlinedInput-root': { height: 36 } }}
                      />
                      <IconButton size="small" onClick={handleCreateCentro} sx={{ color: GREEN }}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setAddingCentro(false); setNovoCentroNome(''); }} sx={{ color: '#999' }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ) : (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setAddingCentro(true)}
                      sx={{ alignSelf: 'flex-start', color: BLUE, '&:hover': { bgcolor: 'rgba(59,130,246,0.06)' } }}
                    >
                      Novo Centro de Custo
                    </Button>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Container>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Excluir a categoria "{confirmDelete?.nome}"? Lançamentos vinculados não serão afetados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(confirmDelete?.id)}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDeleteCentro} onClose={() => setConfirmDeleteCentro(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Excluir o centro de custo "{confirmDeleteCentro?.nome}"? Lançamentos vinculados não serão afetados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteCentro(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => handleDeleteCentro(confirmDeleteCentro?.id)}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
